import { useEffect, useState } from "react";
import { keyVault } from "../../platform/vault/index.js";
import { useT } from "../../i18n/use-t.js";
import { AlertTriangle } from "lucide-react";

/**
 * 키 볼트 보안 설정(결정 #15: 다층 방어의 패스프레이즈 계층).
 *
 * 평상시 키는 Web Crypto 비추출 마스터 키로 암호화된다. 여기서 패스프레이즈를
 * 켜면 PBKDF2 파생 키로 재암호화하여 공유기기/분실에 대비한다(이때만 동기화
 * 옵트인이 가능, 결정 #15/#20).
 *
 * 정직한 고지(결정 #15): 패스프레이즈를 분실하면 복구할 수 없다. 순수 클라이언트
 * 앱이라 완벽히 숨길 수는 없고, 위험을 낮출 뿐이다.
 */
export function SecuritySection() {
  const { t } = useT();
  const [hasPass, setHasPass] = useState<boolean | null>(null);
  const [mode, setMode] = useState<"idle" | "set" | "change" | "remove">("idle");

  const refresh = async () => {
    setHasPass(await keyVault.hasPassphrase());
  };
  useEffect(() => {
    void refresh();
  }, []);

  return (
    <section>
      <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-faint">
        {t("sec.title")}
      </h2>
      <div className="space-y-2 rounded-xl border border-line bg-bg-soft p-3">
        <p className="text-xs text-ink-faint">
          {hasPass === null
            ? t("sec.checking")
            : hasPass
              ? t("sec.statusSet")
              : t("sec.statusNone")}
        </p>

        {mode === "idle" && (
          <div className="flex flex-wrap gap-2">
            {!hasPass ? (
              <button
                className="rounded-lg bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-soft"
                onClick={() => setMode("set")}
              >
                {t("sec.setPassphrase")}
              </button>
            ) : (
              <>
                <button
                  className="rounded-lg border border-line px-3 py-1.5 text-sm text-ink-soft hover:bg-bg-raised"
                  onClick={() => setMode("change")}
                >
                  {t("sec.change")}
                </button>
                <button
                  className="rounded-lg border border-line px-3 py-1.5 text-sm text-red-300 hover:bg-bg-raised"
                  onClick={() => setMode("remove")}
                >
                  {t("sec.remove")}
                </button>
              </>
            )}
          </div>
        )}

        {mode !== "idle" && (
          <PassphraseForm
            mode={mode}
            onDone={async () => {
              setMode("idle");
              await refresh();
            }}
            onCancel={() => setMode("idle")}
          />
        )}

        <p className="flex items-start gap-1.5 pt-1 text-xs text-ink-faint">
          <AlertTriangle size={14} aria-hidden="true" className="mt-0.5 shrink-0" />
          {t("sec.lossWarning")}
        </p>
      </div>
    </section>
  );
}

function PassphraseForm({
  mode,
  onDone,
  onCancel,
}: {
  mode: "set" | "change" | "remove";
  onDone: () => void | Promise<void>;
  onCancel: () => void;
}) {
  const { t } = useT();
  const [oldPass, setOldPass] = useState("");
  const [newPass, setNewPass] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const needsOld = mode === "change" || mode === "remove";
  const needsNew = mode === "set" || mode === "change";

  const submit = async () => {
    setErr(null);
    if (needsNew && newPass !== confirm) {
      setErr(t("sec.errMismatch"));
      return;
    }
    if (needsNew && newPass.length < 4) {
      setErr(t("sec.errTooShort"));
      return;
    }
    setBusy(true);
    try {
      if (mode === "remove") {
        await keyVault.removePassphrase(oldPass);
      } else {
        await keyVault.setPassphrase(needsOld ? oldPass : undefined, newPass);
      }
      await onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2 border-t border-line pt-2">
      {needsOld && (
        <Field label={t("sec.currentLabel")}>
          <input
            type="password"
            autoComplete="off"
            className="w-full rounded-lg border border-line bg-bg-raised px-2 py-1.5 text-sm text-ink outline-none focus:border-accent"
            value={oldPass}
            onChange={(e) => setOldPass(e.target.value)}
          />
        </Field>
      )}
      {needsNew && (
        <>
          <Field label={t("sec.newLabel")}>
            <input
              type="password"
              autoComplete="new-password"
              className="w-full rounded-lg border border-line bg-bg-raised px-2 py-1.5 text-sm text-ink outline-none focus:border-accent"
              value={newPass}
              onChange={(e) => setNewPass(e.target.value)}
            />
          </Field>
          <Field label={t("sec.confirmLabel")}>
            <input
              type="password"
              autoComplete="new-password"
              className="w-full rounded-lg border border-line bg-bg-raised px-2 py-1.5 text-sm text-ink outline-none focus:border-accent"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </Field>
        </>
      )}
      {err && <p className="text-xs text-red-300">{err}</p>}
      <div className="flex justify-end gap-2">
        <button
          className="rounded-lg border border-line px-3 py-1.5 text-sm text-ink-soft hover:bg-bg-raised"
          onClick={onCancel}
          disabled={busy}
        >
          {t("common.cancel")}
        </button>
        <button
          className="rounded-lg bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-soft disabled:opacity-50"
          onClick={submit}
          disabled={busy}
        >
          {busy ? t("sec.processing") : mode === "remove" ? t("sec.remove") : t("common.save")}
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs text-ink-soft">{label}</span>
      {children}
    </label>
  );
}
